import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { HistoricalPerformanceChartV2 } from "shared/Chart/HistoricalPerformanceChart/v2/index";
import {
  getHpLineColors,
  getHpFilteredDataWithTimeframe,
  stripBenchmarkFromHpData,
} from "features/Strategy/utils";
import { useStrategyDetails } from "features/Strategy/hook/useStrategyDetails";
import { parseDateISO } from "utils/date";
import { COLOR_LIGHTER_GREY2 } from "style/v2/theme";

const StrategyChart = ({ strategyId, isPublic = false }) => {
  const [chartLineColors, setChartLineColors] = useState([]);
  const [activeHpFilter, setActiveHpFilter] = useState("Max");
  const [hPerformance, setHPerformance] = useState({});

  const { data: strategyDetailData, isLoading: isDetailsLoading } =
    useStrategyDetails(strategyId, isPublic);

  // Dashed marker showing where the live inception date falls on the
  // performance timeline; shown only when there's data before that date.
  const inceptionReferenceLineProps = useMemo(() => {
    const activeData = hPerformance[activeHpFilter];
    const inception = parseDateISO(strategyDetailData?.live_inception_date);
    if (!inception || !Array.isArray(activeData) || activeData.length === 0) {
      return null;
    }

    const inceptionSeconds = Math.floor(inception.getTime() / 1000);
    // Snap to the first point on/after inception so the line sits on a real x category.
    const hasDataBeforeInception = activeData.some(
      (d) => d.name < inceptionSeconds
    );
    const inceptionPoint = activeData.find((d) => d.name >= inceptionSeconds);
    if (!hasDataBeforeInception || !inceptionPoint) return null;

    return {
      x: inceptionPoint.name,
      stroke: COLOR_LIGHTER_GREY2,
      strokeDasharray: "6 6",
      label: {
        value: "Inception Date",
        position: "insideTopRight",
        fill: COLOR_LIGHTER_GREY2,
        fontSize: 11,
      },
    };
  }, [hPerformance, activeHpFilter, strategyDetailData?.live_inception_date]);

  useEffect(() => {
    if (strategyDetailData?.overview_chart) {
      // Process the API data using the same utilities as StrategyDetail
      const processedData = getHpFilteredDataWithTimeframe(
        strategyDetailData.overview_chart
      );
      const processedDataWithoutBenchmark =
        stripBenchmarkFromHpData(processedData);
      setHPerformance(processedDataWithoutBenchmark);

      // Generate chart line colors based on the current filter data
      if (
        processedDataWithoutBenchmark &&
        processedDataWithoutBenchmark[activeHpFilter]
      ) {
        const lineColors = getHpLineColors(
          processedDataWithoutBenchmark[activeHpFilter],
          false
        );
        setChartLineColors(lineColors);
      }
    }
  }, [strategyDetailData?.overview_chart, activeHpFilter]);

  const handleFilterClick = (filterId) => {
    setActiveHpFilter(filterId);
  };

  return (
    <HistoricalPerformanceChartV2
      height="300px"
      data={hPerformance[activeHpFilter] || []}
      lines={chartLineColors}
      activePeriod={activeHpFilter}
      handleFilterClick={handleFilterClick}
      timeframe={["3M", "YTD", "1Y", "3Y", "5Y", "Max"]}
      isLoading={isDetailsLoading}
      showLegend={false}
      periodReturns={strategyDetailData?.period_returns}
      {...(inceptionReferenceLineProps && {
        referenceLineProps: inceptionReferenceLineProps,
        // Hidden "name" axis: lets the ReferenceLine `x` line up with the data.
        xAxisProps: { dataKey: "name", hide: true },
      })}
    />
  );
};

StrategyChart.propTypes = {
  strategyId: PropTypes.string.isRequired,
  isPublic: PropTypes.bool,
};

export default StrategyChart;
